import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Hero from './components/Hero';
import AboutPreview from './components/AboutPreview';
import UpcomingEvents from './components/UpcomingEvents';
import FeaturedProjects from './components/FeaturedProjects';
import SocietyHighlights from './components/SocietyHighlights.tsx';
import PastEvents from './components/PastEvents.tsx';
import CallToAction from './components/CallToAction';
import Footer from './components/Footer';
import LoadingScreen from './components/LoadingScreen';
import ScrollToTop from './components/ScrollToTop';
import QuizNotifier from './components/QuizNotifier';

import './App.css';
import './index.css';
import './JarvisTheme.css';

// Lazy load secondary pages
const Careers = lazy(() => import('./components/Careers'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const ReadOnlyAdminPanel = lazy(() => import('./components/ReadOnlyAdminPanel'));
const Team = lazy(() => import('./components/Team'));
// const Projects = lazy(() => import('./components/Projects'));
const IMCDay1 = lazy(() => import('./components/IMCDay1'));
const IMCDay2 = lazy(() => import('./components/IMCDay2'));
const DucatVisit = lazy(() => import('./components/DucatVisit'));
const WebDesignRegistration = lazy(() => import('./components/WebDesignRegistration'));
const QuizRegistration = lazy(() => import('./components/QuizRegistration'));
const QuizLogin = lazy(() => import('./components/QuizLogin'));
const QuizLobby = lazy(() => import('./components/QuizLobby'));
const QuizPage = lazy(() => import('./components/QuizPage'));
const QuizResult = lazy(() => import('./components/QuizResult'));
const QuizLeaderboard = lazy(() => import('./components/QuizLeaderboard'));
const FeedbackForm = lazy(() => import('./components/FeedbackForm'));
const FeedbackView = lazy(() => import('./components/FeedbackView'));
const ExperienceLetter = lazy(() => import('./components/ExperienceLetter'));
const VerifyExperienceLetter = lazy(() => import('./components/VerifyExperienceLetter'));
const EventRegistrationForm = lazy(() => import('./components/EventRegistrationForm'));

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [isLoading, setIsLoading] = useState(true);
  const [isApplicationDashboardOpen, setIsApplicationDashboardOpen] = useState(false);
  const audioRef = useRef(null);

  // Audio playback removed due to missing file and browser autoplay blocks
  // useEffect(() => {
  //   console.log('Setting up automatic audio...');

  //   // Create audio element with the working path
  //   const audio = new Audio('/Jarvis voice.mp3');
  //   audioRef.current = audio;

  //   // Configure audio settings
  //   audio.volume = 1.0; // Set volume to 100% (full volume)
  //   audio.preload = 'auto';

  //   // Track if audio has been played to prevent multiple plays
  //   let hasPlayed = false;

  //   // Function to play audio (only once per session)
  //   const playWelcomeAudio = () => {
  //     if (hasPlayed) {
  //       console.log('Audio already played, skipping...');
  //       return;
  //     }

  //     console.log('Playing welcome audio...');
  //     hasPlayed = true;

  //     if (audio) {
  //       audio.currentTime = 0; // Reset to beginning
  //       audio.play().then(() => {
  //         console.log('Welcome audio played successfully!');
  //       }).catch(error => {
  //         console.error('Audio playback failed:', error);
  //         hasPlayed = false; // Reset flag if play failed
  //       });
  //     }
  //   };

  //   // Add event listeners to catch user interaction
  //   document.addEventListener('click', playWelcomeAudio);
  //   document.addEventListener('keydown', playWelcomeAudio);
  //   document.addEventListener('touchstart', playWelcomeAudio);

  //   // Try autoplay once after a short delay
  //   setTimeout(() => {
  //     if (!hasPlayed) {
  //       console.log('Attempting autoplay...');
  //       audio.play().then(() => {
  //         console.log('Autoplay successful!');
  //         hasPlayed = true;
  //       }).catch(error => {
  //         console.log('Autoplay failed:', error.message);
  //       });
  //     }
  //   }, 1000);

  //   // Cleanup function
  //   return () => {
  //     document.removeEventListener('click', playWelcomeAudio);
  //     document.removeEventListener('keydown', playWelcomeAudio);
  //     document.removeEventListener('touchstart', playWelcomeAudio);
  //     if (audioRef.current) {
  //       audioRef.current.pause();
  //       audioRef.current = null;
  //     }
  //   };
  // }, []); // Empty dependency array means this runs on every page load/refresh

  // Check URL path on component mount and route changes
  useEffect(() => {
    const path = window.location.pathname;
    if (path === '/admin') {
      setCurrentPage('admin');
    } else if (path === '/admin-view') {
      setCurrentPage('admin_view');
    } else if (path === '/careers') {
      setCurrentPage('careers');
    } else if (path === '/team') {
      setCurrentPage('team');
    // } else if (path === '/projects') {
    //   setCurrentPage('projects');
    } else if (path === '/events') {
      setCurrentPage('home');
      // After mount, scroll to events section
      setTimeout(() => {
        const el = document.getElementById('events');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    } else if (path === '/imc-day1') {
      setCurrentPage('imc_day1');
    } else if (path === '/imc-day2') {
      setCurrentPage('imc_day2');
    } else if (path === '/expert-talk') {
      setCurrentPage('ducat_visit');
    } else if (path === '/quiz-registration') {
      setCurrentPage('quiz_registration');
    } else if (path === '/quiz') {
      setCurrentPage('quiz_page');
    } else if (path === '/login') {
      setCurrentPage('quiz_login');
    } else if (path === '/lobby') {
      setCurrentPage('quiz_lobby');
    } else if (path === '/quiz-result') {
      setCurrentPage('quiz_result');
    } else if (path === '/leaderboard') {
      setCurrentPage('quiz_leaderboard');
    } else if (path === '/web-design') {
      setCurrentPage('web_design');
    } else if (path === '/experience-letter') {
      setCurrentPage('experience-letter');
    } else if (path === '/verify-certificate') {
      setCurrentPage('verify-certificate');
    } else if (path === '/feedback') {
      setCurrentPage('feedback');
    } else if (path === '/feedback-view') {
      setCurrentPage('feedback_view');
    } else if (path === '/webdesign-feedback') {
      // setCurrentPage('webdesign_feedback');
    } else if (path.startsWith('/event-register/')) {
      const eventId = path.replace('/event-register/', '');
      if (eventId) setCurrentPage(`event_register_${eventId}`);
    } else {
      setCurrentPage('home');
    }
  }, []);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path === '/admin') {
        setCurrentPage('admin');
      } else if (path === '/admin-view') {
        setCurrentPage('admin_view');
      } else if (path === '/careers') {
        setCurrentPage('careers');
      } else if (path === '/team') {
        setCurrentPage('team');
      // } else if (path === '/projects') {
      //   setCurrentPage('projects');
      } else if (path === '/events') {
        setCurrentPage('home');
        setTimeout(() => {
          const el = document.getElementById('events');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } else if (path === '/imc-day1') {
        setCurrentPage('imc_day1');
      } else if (path === '/imc-day2') {
        setCurrentPage('imc_day2');
      } else if (path === '/expert-talk') {
        setCurrentPage('ducat_visit');
      } else if (path === '/quiz-registration') {
        setCurrentPage('quiz_registration');
      } else if (path === '/quiz') {
        setCurrentPage('quiz_page');
      } else if (path === '/login') {
        setCurrentPage('quiz_login');
      } else if (path === '/lobby') {
        setCurrentPage('quiz_lobby');
      } else if (path === '/quiz-result') {
        setCurrentPage('quiz_result');
      } else if (path === '/leaderboard') {
        setCurrentPage('quiz_leaderboard');
      } else if (path === '/web-design') {
        setCurrentPage('web_design');
      } else if (path === '/experience-letter') {
        setCurrentPage('experience-letter');
      } else if (path === '/verify-certificate') {
        setCurrentPage('verify-certificate');
      } else if (path === '/feedback') {
        setCurrentPage('feedback');
      } else if (path === '/feedback-view') {
        setCurrentPage('feedback_view');
      } else if (path === '/webdesign-feedback') {
        // setCurrentPage('webdesign_feedback');
      } else if (path.startsWith('/event-register/')) {
        const eventId = path.replace('/event-register/', '');
        if (eventId) setCurrentPage(`event_register_${eventId}`);
      } else {
        setCurrentPage('home');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handlePageChange = (page) => {
    // Update URL without page reload
    if (page === 'admin') {
      window.history.pushState({}, '', '/admin');
    } else if (page === 'admin_view') {
      window.history.pushState({}, '', '/admin-view');
    } else if (page === 'careers') {
      window.history.pushState({}, '', '/careers');
    } else if (page === 'team') {
      window.history.pushState({}, '', '/team');
    // } else if (page === 'projects') {
    //   window.history.pushState({}, '', '/projects');
    } else if (page === 'events') {
      window.history.pushState({}, '', '/events');
      // Stay on home page and scroll to events section
      setCurrentPage('home');
      setTimeout(() => {
        const el = document.getElementById('events');
        if (el) {
          const headerHeight = document.querySelector('.header')?.offsetHeight || 80;
          window.scrollTo({ top: el.offsetTop - headerHeight - 10, behavior: 'smooth' });
        }
      }, 50);
      return;
    } else if (page === 'imc_day1') {
      window.history.pushState({}, '', '/imc-day1');
    } else if (page === 'imc_day2') {
      window.history.pushState({}, '', '/imc-day2');
    } else if (page === 'ducat_visit') {
      window.history.pushState({}, '', '/expert-talk');

    } else if (page === 'quiz_registration') {
      window.history.pushState({}, '', '/quiz-registration');
    } else if (page === 'quiz_page') {
      window.history.pushState({}, '', '/quiz');
    } else if (page === 'quiz_login') {
      window.history.pushState({}, '', '/login');
    } else if (page === 'quiz_lobby') {
      window.history.pushState({}, '', '/lobby');
    } else if (page === 'quiz_result') {
      window.history.pushState({}, '', '/quiz-result');
    } else if (page === 'quiz_leaderboard') {
      window.history.pushState({}, '', '/leaderboard');
    } else if (page === 'web_design') {
      window.history.pushState({}, '', '/web-design');
    } else if (page === 'experience-letter') {
      window.history.pushState({}, '', '/experience-letter');
    } else if (page === 'verify-certificate') {
      window.history.pushState({}, '', '/verify-certificate');
    } else if (page === 'feedback') {
      window.history.pushState({}, '', '/feedback');
    } else if (page === 'feedback_view') {
      window.history.pushState({}, '', '/feedback-view');
    } else if (page === 'webdesign_feedback') {
      // window.history.pushState({}, '', '/webdesign-feedback');
    } else if (page.startsWith('event_register_')) {
      const eventId = page.replace('event_register_', '');
      window.history.pushState({}, '', `/event-register/${eventId}`);
    } else {
      window.history.pushState({}, '', '/');
    }

    // Set current page
    setCurrentPage(page);

    // Smooth scroll to top
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  useEffect(() => {
    const navigateToTeam = () => handlePageChange('team');
    window.addEventListener('navigateToTeam', navigateToTeam);
    return () => window.removeEventListener('navigateToTeam', navigateToTeam);
  }, []);

  // Handle anchored section navigation from navbar
  useEffect(() => {
    const navigateToSection = (e) => {
      const { id } = e.detail || {};
      if (!id) return;

      // Ensure we're on home page for section navigation
      if (currentPage !== 'home') {
        setCurrentPage('home');
        // Preserve canonical /events path if already there
        if (window.location.pathname === '/events') {
          // keep /events and just scroll
        } else {
          window.history.pushState({}, '', `/#${id}`);
        }
        // Wait for page change then scroll
        setTimeout(() => {
          const el = document.getElementById(id);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        // Already on home page, just scroll
        if (window.location.pathname !== '/events') {
          window.history.replaceState({}, '', `/#${id}`);
        }
        const el = document.getElementById(id);
        if (el) {
          const headerHeight = document.querySelector('.header')?.offsetHeight || 80;
          const elementTop = el.offsetTop - headerHeight - 10;

          // Use requestAnimationFrame for smoother scrolling
          requestAnimationFrame(() => {
            window.scrollTo({
              top: elementTop,
              behavior: 'smooth'
            });
          });
        }
      }
    };
    window.addEventListener('navigateToSection', navigateToSection);
    return () => window.removeEventListener('navigateToSection', navigateToSection);
  }, [currentPage]);

  // Handle application dashboard visibility (hide navbar)
  useEffect(() => {
    const handleDashboardToggle = (e) => {
      const { isOpen } = e.detail || {};
      setIsApplicationDashboardOpen(isOpen);
    };

    window.addEventListener('applicationDashboardToggled', handleDashboardToggle);
    return () => {
      window.removeEventListener('applicationDashboardToggled', handleDashboardToggle);
    };
  }, []);

  // Scroll to top on initial page load
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  // Cleanup audio when component unmounts
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const renderPage = () => {
    switch (currentPage) {
      case 'careers':
        return <Careers />;
      case 'admin':
        return <AdminPanel />;
      case 'admin_view':
        return <ReadOnlyAdminPanel />;
      case 'team':
        return <Team />;
      // case 'projects':
      //   return <Projects />;
      case 'imc_day1':
        return <IMCDay1 />;
      case 'imc_day2':
        return <IMCDay2 />;
      case 'ducat_visit':
        return <DucatVisit />;

      case 'quiz_registration':
        return <QuizRegistration />;
      case 'quiz_login':
        return <QuizLogin />;
      case 'quiz_lobby':
        return <QuizLobby />;
      case 'quiz_page':
        return <QuizPage />;
      case 'quiz_result':
        return <QuizResult />;
      case 'quiz_leaderboard':
        return <QuizLeaderboard />;
      case 'web_design':
        return <WebDesignRegistration />;
      case 'feedback':
        return <FeedbackForm />;
      case 'feedback_view':
        return <FeedbackView />;
      case 'experience-letter':
        return <ExperienceLetter />;
      case 'verify-certificate':
        return <VerifyExperienceLetter />;
      case 'webdesign_feedback':
        // return <WebDesignFeedback />;
        return null;
      default:
        // Check for dynamic event registration pages (e.g. event_register_abc123)
        if (currentPage.startsWith('event_register_')) {
          const eventId = currentPage.replace('event_register_', '');
          return <EventRegistrationForm eventId={eventId} onPageChange={handlePageChange} />;
        }
        return (
          <>
            <Hero onPageChange={handlePageChange} />
            <AboutPreview />
            <UpcomingEvents onPageChange={handlePageChange} />
            <FeaturedProjects onPageChange={handlePageChange} />
            <PastEvents />
            <SocietyHighlights />
            {/* <Testimonials /> */}
            <CallToAction />
          </>
        );
    }
  };

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  // Get canonical URL based on current page
  const getCanonicalUrl = () => {
    const baseUrl = 'https://aiec-iitm.com';
    switch (currentPage) {
      case 'admin':
        return `${baseUrl}/admin`;
      case 'admin_view':
        return `${baseUrl}/admin-view`;
      case 'careers':
        return `${baseUrl}/careers`;
      case 'team':
        return `${baseUrl}/team`;
      case 'projects':
        return `${baseUrl}/projects`;
      case 'imc_day1':
        return `${baseUrl}/imc-day1`;
      case 'imc_day2':
        return `${baseUrl}/imc-day2`;
      case 'ducat_visit':
        return `${baseUrl}/expert-talk`;
      case 'quiz_registration':
        return `${baseUrl}/quiz-registration`;
      case 'quiz_page':
        return `${baseUrl}/quiz`;
      case 'quiz_login':
        return `${baseUrl}/login`;
      case 'quiz_lobby':
        return `${baseUrl}/lobby`;
      case 'quiz_result':
        return `${baseUrl}/quiz-result`;
      case 'quiz_leaderboard':
        return `${baseUrl}/leaderboard`;
      case 'web_design':
        return `${baseUrl}/web-design`;
      case 'experience-letter':
        return `${baseUrl}/experience-letter`;
      case 'verify-certificate':
        return `${baseUrl}/verify-certificate`;
      case 'feedback':
        return `${baseUrl}/feedback`;
      case 'feedback_view':
        return `${baseUrl}/feedback-view`;
      case 'webdesign_feedback':
        return `${baseUrl}/webdesign-feedback`;
      default:
        // For home page, check if on /events
        if (window.location.pathname === '/events') {
          return `${baseUrl}/events`;
        }
        return baseUrl;
    }
  };

  // Get page title based on current page
  const getPageTitle = () => {
    switch (currentPage) {
      case 'careers':
        return 'Careers - AIEC IITM | Join Our Technical Society';
      case 'team':
        return 'Our Team - AIEC IITM | Meet the Leaders';
      case 'projects':
        return 'Projects - AIEC IITM | Innovation & Technology';
      case 'imc_day1':
        return 'IMC Day 1 - AIEC IITM | Event Highlights';
      case 'imc_day2':
        return 'IMC Day 2 - AIEC IITM | Event Highlights';
      case 'ducat_visit':
        return 'Expert Talk - AIEC IITM | Hands-on Session with Tableau';
      case 'quiz_registration':
        return 'Quiz Competition - AIEC IITM | Register';
      case 'quiz_login':
        return 'Quiz Login - AIEC IITM | Access Quiz';
      case 'quiz_lobby':
        return 'Quiz Lobby - AIEC IITM | Waiting Room';
      case 'quiz_page':
        return 'Quiz - AIEC IITM | HTML & CSS Quiz';
      case 'quiz_result':
        return 'Quiz Result - AIEC IITM | Your Score';
      case 'quiz_leaderboard':
        return 'Quiz Leaderboard - AIEC IITM | Admin Panel';
      case 'web_design':
        return 'Web Design Competition - AIEC IITM | Register';
      case 'feedback':
        return 'Feedback - AIEC IITM | Share Your Experience';
      case 'feedback_view':
        return 'Feedback View - AIEC IITM | Admin Access';
      case 'webdesign_feedback':
        return 'Web Design Feedback - AIEC IITM | Cerebro Challenge 2025';
      case 'experience-letter':
        return 'Experience Letter - AIEC IITM | Generate Your Official Certificate';
      case 'verify-certificate':
        return 'Verify Certificate - AIEC IITM | Authentic Experience Letter';
      default:
        return 'AIEC IITM - Premier Technical Society | AI, ML, Innovation & Technology';
    }
  };

  // Get page description based on current page
  const getPageDescription = () => {
    switch (currentPage) {
      case 'careers':
        return 'Join AIEC IITM and be part of a thriving technical community. Explore career opportunities and contribute to cutting-edge projects.';
      case 'team':
        return 'Meet the dedicated team behind AIEC IITM. Our leaders and members driving innovation in AI, ML, and technology.';
      case 'projects':
        return 'Explore innovative projects by AIEC IITM members. From AI applications to cutting-edge technology solutions.';
      case 'imc_day1':
        return 'IMC Day 1 highlights and coverage from AIEC IITM. Innovation, technology, and collaboration.';
      case 'imc_day2':
        return 'IMC Day 2 highlights and coverage from AIEC IITM. Innovation, technology, and collaboration.';
      case 'ducat_visit':
        return 'Interactive hands-on session with Tableau by AIEC IITM. Learn data visualization and analytics with expert guidance.';
      case 'quiz_registration':
        return 'Register for the Quiz Competition by AIEC IITM. BCA 1st Year only. Test your fundamentals and speed.';
      case 'quiz_login':
        return 'Login to access the Quiz Competition by AIEC IITM. Enter your enrollment number and phone number to continue.';
      case 'quiz_lobby':
        return 'Quiz Lobby - Wait here for the quiz to begin. Your registration has been verified and you are ready to participate.';
      case 'quiz_page':
        return 'Quiz - Test your HTML and CSS knowledge with multiple choice questions. Answer carefully and submit within the time limit.';
      case 'quiz_result':
        return 'Quiz Result - View your quiz score and performance. See how well you did in the HTML and CSS quiz.';
      case 'quiz_leaderboard':
        return 'Quiz Admin Panel - Manage quiz settings, monitor players, and control quiz flow. Password protected admin access.';
      case 'web_design':
        return 'Register for the Web Design Competition by AIEC IITM. BCA 1st Year only. Show your creativity and frontend skills.';
      case 'experience-letter':
        return 'Generate and download official AIEC IITM experience letters. Professional certificates for our dedicated team members.';
      case 'verify-certificate':
        return 'Verify and view your official AIEC IITM experience letters using your reference ID.';
      case 'feedback':
        return 'Share your feedback about AIEC IITM events. Your opinion helps us improve and deliver better experiences.';
      case 'feedback_view':
        return 'Admin access to view all feedback submissions for AIEC IITM events. Password protected area.';
      default:
        return 'AIEC IITM is the premier technical society at IITM Janakpuri, fostering innovation in AI, Machine Learning, and technology. Join us for hackathons, projects, and cutting-edge research.';
    }
  };

  return (
    <HelmetProvider>
      <div className="App">
        <Helmet>
          <title>{getPageTitle()}</title>
          <meta name="description" content={getPageDescription()} />
          <link rel="canonical" href={getCanonicalUrl()} />
          <meta property="og:url" content={getCanonicalUrl()} />
          <meta property="og:title" content={getPageTitle()} />
          <meta property="og:description" content={getPageDescription()} />
          <meta property="twitter:url" content={getCanonicalUrl()} />
          <meta property="twitter:title" content={getPageTitle()} />
          <meta property="twitter:description" content={getPageDescription()} />
        </Helmet>

        {/* ShaderBackground removed from here - only in testimonials now */}

        <LoadingScreen
          isLoading={isLoading}
          onLoadingComplete={handleLoadingComplete}
        />
        {!isLoading && (
          <>
            {(!isApplicationDashboardOpen && ![
              'quiz_login',
              'quiz_lobby',
              'quiz_page',
              'quiz_result',
              'quiz_leaderboard',
              'experience-letter',
              'verify-certificate'
            ].includes(currentPage)) && (
                <>
                  <Header onPageChange={handlePageChange} currentPage={currentPage} />
                </>
              )}
            {currentPage === 'home' && <QuizNotifier />}
            <main>
              <Suspense fallback={<div className="min-h-screen bg-black" />}>
                {renderPage()}
              </Suspense>
            </main>
            {(!['experience-letter', 'verify-certificate'].includes(currentPage)) && <Footer />}

            {(!['experience-letter', 'verify-certificate'].includes(currentPage)) && <ScrollToTop />}
          </>
        )}
      </div>
    </HelmetProvider>
  );
}

export default App;